import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private readonly _httpClient = inject(HttpClient);
  private readonly _localhostUrl = 'http://localhost:5001';

  public get(path: string, params: HttpParams | {} = new HttpParams(), useLocalhost = false): Observable<any> {
    const baseUrl = useLocalhost ? this._localhostUrl : environment.apiUrl;
    return this._httpClient.get(`${ baseUrl }${ path }`, { params });
  }

  public post(
    path: string,
    body: Object = {},
    params: HttpParams = new HttpParams(),
    responseType?: string,
    observe: 'body' = 'body',
    useLocalhost = false
  ): Observable<any> {
    const baseUrl = useLocalhost ? this._localhostUrl : environment.apiUrl;
    const options = {
      params: params,
      observe: observe,
      headers: {
        'Content-Type': 'application/json'
      },
    };

    if (responseType) {
      options['responseType'] = responseType;
    }

    return this._httpClient.post(`${ baseUrl }${ path }`, JSON.stringify(body), options);
  }

  public postFormData(path: string, formData: FormData, params: HttpParams = new HttpParams(), useLocalhost = false): Observable<any> {
    const baseUrl = useLocalhost ? this._localhostUrl : environment.apiUrl;
    return this._httpClient.post(
      `${ baseUrl }${ path }`,
      formData,
      {
        params,
      }
    );
  }

  public uploadFile(path: string, formData: FormData, params: HttpParams = new HttpParams(), useLocalhost = false): Observable<any> {
    const baseUrl = useLocalhost ? this._localhostUrl : environment.apiUrl;
    return this._httpClient.post(
      `${ baseUrl }${ path }`,
      formData,
      {
        params,
        observe: 'events',
        reportProgress: true
      }
    );
  }

  public uploadFileToPredefinedUrl(url: string, file: File, params: HttpParams = new HttpParams()): Observable<any> {
    return this._httpClient.put(url, file, {
      params,
      observe: 'events',
      reportProgress: true,
      headers: {
        'Content-Type': file.type,
        'x-amz-acl': 'public-read',
      }
    });
  }

  public getFile(path: string, contentType: string, params: HttpParams = new HttpParams(), useLocalhost = false): Observable<Blob> {
    const baseUrl = useLocalhost ? this._localhostUrl : environment.apiUrl;
    return this._httpClient.get(`${ baseUrl }${ path }`,
      {
        params,
        headers: {
          'Accept': contentType
        },
        responseType : 'blob',
      },
    );
  }

  public put(path: string, body = {}, useLocalhost = false): Observable<any> {
    const baseUrl = useLocalhost ? this._localhostUrl : environment.apiUrl;
    return this._httpClient.put(
      `${ baseUrl }${ path }`,
      JSON.stringify(body),
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
  }

  public patch(path: string, body = {}, useLocalhost = false): Observable<any> {
    const baseUrl = useLocalhost ? this._localhostUrl : environment.apiUrl;
    return this._httpClient.patch(
      `${ baseUrl }${ path }`,
      JSON.stringify(body),
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
  }

  public delete(path: string, useLocalhost = false): Observable<any> {
    const baseUrl = useLocalhost ? this._localhostUrl : environment.apiUrl;
    return this._httpClient.delete(`${ baseUrl }${ path }`);
  }
}
